import { UseFormMethods } from 'react-hook-form';
import { BeneficiaryDetailsData, BeneficiaryDetailsInputs, BeneficiaryDetailsProps } from './beneficiary-details';
import React, { useEffect, useState } from 'react';
import { StyledBeneficiaryCol } from 'beneficiaries/styles/beneficiary-details.styles';
import { Row, Col } from 'reactstrap';
import { InputField, InputFieldDisplayProperties } from 'rootstrap/components/forms/new-fields/input-field';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { getWording } from 'site-config';
import { useSiteConfigContext } from 'style-context';
import { CountrySelectField } from 'rootstrap/components/forms/new-fields/extended-components/country-select-field';
import { CountryCode } from 'libphonenumber-js';
import { getDefaultCountryCode } from 'shared/utils';

interface BeneficiaryAddressFieldsProps extends BeneficiaryDetailsProps {
  form: UseFormMethods<Partial<BeneficiaryDetailsData>>;
}

export const BeneficiaryAddressFields = (props: BeneficiaryAddressFieldsProps) => {
  const { form, activeElement, beneficiary, setActiveElement } = props;
  const { siteConfig } = useSiteConfigContext();
  const [addressFieldsRequired, setAddressFieldsRequired] = useState(false);

  const formValues = form.watch();
  const country = form.watch(BeneficiaryDetailsInputs.Country);

  useEffect(() => {
    if (
      formValues.addressLine1 ||
      formValues.addressLine2 ||
      formValues.suburb ||
      formValues.city ||
      formValues.areaCode
    ) {
      setAddressFieldsRequired(true);
    } else {
      setAddressFieldsRequired(false);
    }
  }, [formValues]);

  return (
    <>
      <StyledBeneficiaryCol sm={12}>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.line1}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.AddressLine1}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.addressLine1.label })}
              form={form}
              {...steppedComponentsBehavior}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.AddressLine2,
                } as InputFieldDisplayProperties
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.line2}
              validators={[]}
              name={BeneficiaryDetailsInputs.AddressLine2}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.addressLine2.label })}
              form={form}
              {...steppedComponentsBehavior}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Suburb,
                } as InputFieldDisplayProperties
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.suburb}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.Suburb}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.suburb.label })}
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.City,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.city}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.City}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.city.label })}
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Country,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <CountrySelectField
              clearable={true}
              placeholder={undefined}
              defaultValue={beneficiary?.address?.country || getDefaultCountryCode()}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.Country}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.country.label })}
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.AreaCode,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.areaCode}
              validators={[
                ...(addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []),
                {
                  validation: {
                    type: ValidationTypes.AreaCode,
                    props: {
                      countryCode: (country as CountryCode) || 'ZA',
                    },
                  },
                },
              ]}
              name={BeneficiaryDetailsInputs.AreaCode}
              label={getWording({ wording: siteConfig?.inputFields.beneficiaries.areaCode.label })}
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: '',
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
      </StyledBeneficiaryCol>
    </>
  );
};

const steppedComponentsBehavior = {
  isTouched: false,
  disableScrollToElement: false,
  disableActiveElement: false,
  hideBorder: true,
  disableNextButton: true,
  hideDivider: true,
};
